import React from 'react';
import Icon from '../../../components/AppIcon';

const EquipmentInventoryTable = ({ equipment, onEdit, onDelete }) => {
  const getConditionColor = (condition) => {
    const colors = {
      'Sangat Baik': 'text-success bg-success/10',
      'Baik': 'text-success bg-success/10',
      'Cukup': 'text-warning bg-warning/10',
      'Buruk': 'text-error bg-error/10',
      'Kritis': 'text-destructive bg-destructive/10'
    };
    return colors?.[condition] || 'text-muted-foreground bg-muted';
  };

  const getUtilizationColor = (rate) => {
    if (rate >= 80) return 'text-success';
    if (rate >= 60) return 'text-warning';
    return 'text-error';
  };

  return (
    <div className="w-full overflow-hidden">
      {/* Desktop Table View */}
      <div className="hidden lg:block overflow-x-auto">
        <table className="w-full">
          <thead>
            <tr className="border-b border-border">
              <th className="text-left py-4 px-4 text-sm font-semibold text-foreground">Jenis Peralatan</th>
              <th className="text-left py-4 px-4 text-sm font-semibold text-foreground">Kapasitas</th>
              <th className="text-left py-4 px-4 text-sm font-semibold text-foreground">Usia</th>
              <th className="text-left py-4 px-4 text-sm font-semibold text-foreground">Kondisi</th>
              <th className="text-left py-4 px-4 text-sm font-semibold text-foreground">Utilisasi</th>
              <th className="text-left py-4 px-4 text-sm font-semibold text-foreground">Pemeliharaan Terakhir</th>
              <th className="text-right py-4 px-4 text-sm font-semibold text-foreground">Aksi</th>
            </tr>
          </thead>
          <tbody>
            {equipment?.map((item) => (
              <tr key={item?.id} className="border-b border-border hover:bg-muted/50 transition-colors">
                <td className="py-4 px-4">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center flex-shrink-0">
                      <Icon name={item?.icon} size={20} color="var(--color-primary)" />
                    </div>
                    <div>
                      <p className="text-sm font-medium text-foreground">{item?.name}</p>
                      <p className="text-xs text-muted-foreground">{item?.brand}</p>
                    </div>
                  </div>
                </td>
                <td className="py-4 px-4">
                  <p className="text-sm font-medium text-foreground data-text">{item?.capacity}</p>
                  <p className="text-xs text-muted-foreground">{item?.unit}</p>
                </td>
                <td className="py-4 px-4">
                  <p className="text-sm text-foreground data-text">{item?.age} tahun</p>
                </td>
                <td className="py-4 px-4">
                  <span className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium ${getConditionColor(item?.condition)}`}>
                    <Icon name="Activity" size={14} />
                    {item?.condition}
                  </span>
                </td>
                <td className="py-4 px-4">
                  <div className="flex items-center gap-2">
                    <div className="flex-1 h-2 bg-muted rounded-full overflow-hidden">
                      <div 
                        className={`h-full ${getUtilizationColor(item?.utilization)} bg-current transition-all`}
                        style={{ width: `${item?.utilization}%` }}
                      />
                    </div>
                    <span className={`text-sm font-medium data-text ${getUtilizationColor(item?.utilization)}`}>
                      {item?.utilization}%
                    </span>
                  </div>
                </td>
                <td className="py-4 px-4">
                  <p className="text-sm text-foreground">{item?.lastMaintenance}</p>
                </td>
                <td className="py-4 px-4">
                  <div className="flex items-center justify-end gap-2">
                    <button
                      onClick={() => onEdit(item)}
                      className="p-2 rounded-lg hover:bg-muted transition-colors focus-ring"
                      aria-label="Edit equipment"
                    >
                      <Icon name="Edit2" size={16} color="var(--color-primary)" />
                    </button>
                    <button
                      onClick={() => onDelete(item?.id)}
                      className="p-2 rounded-lg hover:bg-destructive/10 transition-colors focus-ring"
                      aria-label="Delete equipment"
                    >
                      <Icon name="Trash2" size={16} color="var(--color-destructive)" />
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {/* Mobile Card View */}
      <div className="lg:hidden space-y-4">
        {equipment?.map((item) => (
          <div key={item?.id} className="bg-card rounded-xl border border-border p-4">
            <div className="flex items-start justify-between mb-4">
              <div className="flex items-center gap-3 flex-1">
                <div className="w-12 h-12 rounded-lg bg-primary/10 flex items-center justify-center flex-shrink-0">
                  <Icon name={item?.icon} size={24} color="var(--color-primary)" />
                </div>
                <div>
                  <p className="text-base font-semibold text-foreground">{item?.name}</p>
                  <p className="text-sm text-muted-foreground">{item?.brand}</p>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => onEdit(item)}
                  className="p-2 rounded-lg hover:bg-muted transition-colors focus-ring"
                  aria-label="Edit equipment"
                >
                  <Icon name="Edit2" size={18} color="var(--color-primary)" />
                </button>
                <button
                  onClick={() => onDelete(item?.id)}
                  className="p-2 rounded-lg hover:bg-destructive/10 transition-colors focus-ring"
                  aria-label="Delete equipment"
                >
                  <Icon name="Trash2" size={18} color="var(--color-destructive)" />
                </button>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4 mb-4">
              <div>
                <p className="text-xs text-muted-foreground mb-1">Kapasitas</p>
                <p className="text-sm font-medium text-foreground data-text">{item?.capacity} {item?.unit}</p>
              </div>
              <div>
                <p className="text-xs text-muted-foreground mb-1">Usia</p>
                <p className="text-sm font-medium text-foreground data-text">{item?.age} tahun</p>
              </div>
              <div>
                <p className="text-xs text-muted-foreground mb-1">Kondisi</p>
                <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg text-xs font-medium ${getConditionColor(item?.condition)}`}>
                  <Icon name="Activity" size={12} />
                  {item?.condition}
                </span>
              </div>
              <div>
                <p className="text-xs text-muted-foreground mb-1">Utilisasi</p>
                <p className={`text-sm font-medium data-text ${getUtilizationColor(item?.utilization)}`}>
                  {item?.utilization}%
                </p>
              </div>
            </div>

            <div className="pt-4 border-t border-border">
              <p className="text-xs text-muted-foreground mb-1">Pemeliharaan Terakhir</p>
              <p className="text-sm text-foreground">{item?.lastMaintenance}</p>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default EquipmentInventoryTable;